/**
 * notification-store — canonical global feedback queue.
 *
 * Consumers call `useNotify()` to push a notification. GlobalNotifications in
 * the application shell renders the queue. Callback identities are stable so
 * effects can safely depend on them without re-emitting feedback on rerender.
 */
import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';

export type NotificationSeverity = 'success' | 'info' | 'warning' | 'error';

export interface Notification {
  id: string;
  message: string;
  severity: NotificationSeverity;
  /** Auto-dismiss duration in ms. 0 = persist until manually dismissed. */
  duration: number;
}

type NotificationInput = Omit<Notification, 'id'>;

const notificationsAtom = atom<Notification[]>([]);

let nextNotificationId = 0;
function nextId(): string {
  return `notif-${++nextNotificationId}`;
}

export const notificationsReadAtom = atom(get => get(notificationsAtom));

export const dismissNotificationAtom = atom(null, (get, set, id: string) => {
  set(
    notificationsAtom,
    get(notificationsAtom).filter(notification => notification.id !== id),
  );
});

export const pushNotificationAtom = atom(null, (get, set, input: NotificationInput) => {
  const notification: Notification = { ...input, id: nextId() };
  set(notificationsAtom, [...get(notificationsAtom), notification]);
});

export function useNotify() {
  const [, push] = useAtom(pushNotificationAtom);
  const [, dismiss] = useAtom(dismissNotificationAtom);

  const notify = useCallback(
    (
      message: string,
      severity: NotificationSeverity = 'info',
      duration = 5000,
    ) => push({ message, severity, duration }),
    [push],
  );

  const dismissNotification = useCallback((id: string) => dismiss(id), [dismiss]);

  return { notify, dismiss: dismissNotification };
}
